import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './styles/mobile-optimizations.css';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import VerifyEmailPage from './pages/VerifyEmailPage';
import EmpfangenPage from './pages/EmpfangenPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MainLayout from './components/layout/MainLayout';
import EnvironmentInfo from './components/debug/EnvironmentInfo';
import CookieConsentBanner from './components/CookieConsent';
import { useAnalyticsSimple } from './hooks/useAnalyticsSimple';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './context/ThemeContext';
// M-UX-0.1 (Z5): globaler Catch-all — s. router-Definition unten. Datei heißt
// bewusst PageNotFound.tsx statt NotFoundPage.tsx (legal-gate-Hook-Substring-
// False-Positiv "dpa" in "FoundPage" — s. JSDoc in der Datei), Komponente
// exportiert weiterhin als NotFoundPage.
import NotFoundPage from './pages/PageNotFound';
// M-UX-1.1: die `/dashboard`-Kind-Routen wurden verhaltensidentisch nach
// `routes/dashboardChildren.tsx` extrahiert (Seiten-Importe, Flag-Gates,
// `AdvisorHidden`-Wrapping leben jetzt dort — s. Datei-Kopf dort für Details).
import { buildDashboardChildren } from './routes/dashboardChildren';

// Marketing-Website Domain (für Redirects)
const MARKETING_DOMAIN = process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com';

// Redirect-Komponente zur Marketing-Website
const RedirectToMarketing: React.FC<{ path: string }> = ({ path }) => {
  useEffect(() => {
    window.location.href = `${MARKETING_DOMAIN}${path}`;
  }, [path]);
  
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="text-primary text-lg">Weiterleitung...</div>
    </div>
  );
};

const router = createBrowserRouter([
  {
    // Redirect root to marketing site or dashboard if logged in
    path: "/",
    element: <RedirectToMarketing path="/" />,
    errorElement: <ErrorBoundary />,
  },
  {
    // Redirect legal pages to marketing site
    path: "/impressum",
    element: <RedirectToMarketing path="/impressum" />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/agb",
    element: <RedirectToMarketing path="/agb" />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/datenschutz", 
    element: <RedirectToMarketing path="/datenschutz" />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/verify-email",
    element: <VerifyEmailPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    // WL-V1: public recruit landing page (no auth). Reached via the marketing
    // redirect /r/{token} → /empfangen?ref={token}. The page is harmless-static;
    // the personalized hook only appears when resolveRecruitToken returns valid
    // (INBOUND_ENABLED on), otherwise the generic landing page renders.
    path: "/empfangen",
    element: <EmpfangenPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    // M-UX-1.1: verhaltensidentisch aus App.tsx extrahiert — s.
    // routes/dashboardChildren.tsx für die vollständige, vormals hier
    // inline definierte Kind-Routen-Liste (Seiten-Elemente, Flag-/Beta-Gates,
    // AdvisorHidden-Wrapping, errorElement je Kind-Route unverändert).
    children: buildDashboardChildren(),
  },
  {
    // M-UX-0.1 (Z5): globaler Catch-all — jede unbekannte URL (inkl. Deep-Links auf
    // gerade nicht registrierte Flag-off-Routen, z. B. /dashboard/scanner-x) zeigt
    // eine ruhige, markengerechte NotFoundPage statt der nackten ErrorBoundary.
    // Bewusst als eigenständige Top-Level-Route (kein Nested-Kind von /dashboard),
    // damit sie auch für nicht-eingeloggte Nutzer/URLs außerhalb von /dashboard greift.
    path: "*",
    element: <NotFoundPage />,
    errorElement: <ErrorBoundary />,
  },
], {
  future: {
    v7_relativeSplatPath: true,
  },
});

function App() {
  // Aktiviere grundlegendes Analytics-Tracking für die gesamte App
  useAnalyticsSimple({ trackUserEngagement: true });
  
  const { ready } = useTranslation();
  
  // Warten bis i18next vollständig geladen ist
  if (!ready) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-primary text-lg">Loading...</div>
      </div>
    );
  }
  
  return (
    // M-D5 Welle 3 (`plan-ux-d5-w3-light-flip.md` §2): ThemeProvider um die
    // gesamte App, damit sowohl der TopBar-Umschalter (MainLayout) als auch
    // die AccountPage-Sektion "Darstellung" denselben Theme-State teilen.
    // Kein Verhaltens-Delta bei Flag off (REACT_APP_UX_THEME_V5_ENABLED) —
    // der Provider selbst ist nur State-Haltung, kein UI.
    <ThemeProvider>
      <EnvironmentInfo />
      {/* M-GA-1: Cookie-Consent-Banner AUSSERHALB des Routers, damit er alle
          Routen abdeckt (inkl. öffentliche /register, /login). Rendert selbst
          null + Portal der Library. Consent-Callback koppelt an analyticsService
          (Bruch B1: der Banner war zuvor nirgends gemountet). */}
      <CookieConsentBanner />
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

export default App;